import React from 'react';
import { t, Trans } from '@lingui/macro';

import { Button, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';
import { HeadingVariant } from '@rover/react-lib/src/components/lists/Carousel/components/Header';
import { EngineEnum } from '@rover/rsdk/src/apiClient/latest/model/engineEnum';
import { SeoLocationDetails } from '@rover/rsdk/src/apiClient/latest/model/seoLocationDetails';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import getSearchUrl from '../../../../utils/getSearchUrl';
import ReviewsCarousel from '../../components/ReviewsCarousel';
import {
  ReviewsCarouselPaginateAction,
  ReviewsCtaClick,
  ViewReviewCard,
} from '../../utilities/analyticsEvents';
import { PageSectionId, useSectionRef } from '../hooks/useSectionRef';

// DEV-144415 remove this in favor of the new get heading function
function getHeading(serviceTypeSlug: ServiceTypeSlugEnum, locationName: string): string {
  switch (serviceTypeSlug) {
    case ServiceTypeSlugEnum['dog-boarding']:
    case ServiceTypeSlugEnum['doggy-day-care']:
    case ServiceTypeSlugEnum['dog-walking']:
    case ServiceTypeSlugEnum['dog-sitting']:
    case ServiceTypeSlugEnum['dog-training']:
      return t`${locationName} dog parents share their Rover experiences`;

    case ServiceTypeSlugEnum['cat-boarding']:
    case ServiceTypeSlugEnum['cat-sitting']:
      return t`${locationName} cat parents share their Rover experiences`;

    case ServiceTypeSlugEnum['drop-in-visits']:
    case ServiceTypeSlugEnum['house-sitting']:
    case ServiceTypeSlugEnum['pet-sitting']:
    case ServiceTypeSlugEnum['pet-boarding']:
    default:
      return t`${locationName} pet parents share their Rover experiences`;
  }
}

const getNewHeader = (petType, brandDisplayName, locationName) => {
  switch (petType) {
    case PetType.CAT:
      return t`Cat parents share their ${brandDisplayName} experiences - ${locationName}`;
    case PetType.DOG:
      return t`Dog parents share their ${brandDisplayName} experiences - ${locationName}`;
    case PetType.PUPPY:
      return t`Puppy parents share their ${brandDisplayName} experiences - ${locationName}`;
    default:
      return t`Pet parents share their ${brandDisplayName} experiences - ${locationName}`;
  }
};
type Props = {
  seoLocation: {
    details: Pick<SeoLocationDetails, 'configuration' | 'name' | 'serviceTypeSlug' | 'petType'>;
  };
  language: string;
  searchFilters: Partial<SearchFilters>;
  headingVariant?: HeadingVariant;
  headingSize?: ThemeHeadingSizes;
} & Pick<React.ComponentProps<typeof ReviewsCarousel>, 'reviews'> &
  Pick<React.ComponentProps<typeof Section>, 'my' | 'mt' | 'mb'>;

const SeoLocationReviewsCarousel: React.FC<Props> = ({
  seoLocation: { details },
  reviews,
  language,
  searchFilters,
  // DEV-144415 change default to heading instead of display
  headingVariant = 'display',
  headingSize,
  ...marginProps
}) => {
  const ref = useSectionRef(PageSectionId.ReviewsCarousel);
  const { petType, name: locationName } = details;
  const brandDisplayName = getBrandDisplayName();

  if (!reviews.length) return null;
  const isTemplateEngine = details.configuration?.engine === EngineEnum.template;

  return (
    <Section htmlRef={ref} paddingLeft="4x" {...marginProps}>
      <Flex gap="6x" flexDirection="column" alignItems="center">
        <ReviewsCarousel
          heading={
            isTemplateEngine
              ? getNewHeader(petType, brandDisplayName, locationName)
              : getHeading(details.serviceTypeSlug, details.name)
          }
          headingVariant={headingVariant}
          headingSize={headingSize}
          reviews={reviews}
          onCardViewed={(index) => emitAnalyticsEvent(new ViewReviewCard({ index }))}
          cardProps={{
            href: getSearchUrl(searchFilters, language),
            hrefLabel: t`Find pet care near you`,
          }}
          onPaginate={(paginateParams) =>
            emitAnalyticsEvent(
              new ReviewsCarouselPaginateAction({
                serviceTypeSlug: details.serviceTypeSlug,
                ...paginateParams,
              })
            )
          }
        />
        <Button
          variant="default"
          width="100%"
          maxWidth={['335px', '375px']}
          href={getSearchUrl(searchFilters, language)}
          onPress={() => {
            new ReviewsCtaClick({
              serviceTypeSlug: details.serviceTypeSlug,
            }).fireCrossPageEvent();
          }}
        >
          <Trans>Find pet care near you</Trans>
        </Button>
      </Flex>
    </Section>
  );
};

export default SeoLocationReviewsCarousel;
